<template>
  <div class="page-view">

    <BasePageHeader :title="$t('projects.title')" @back="$router.back()">
      <template #actions>
        <router-link v-if="isOperatorOrAbove" to="/bulk-edit/projects" class="btn ghost small">{{ $t('bulkEdit.title') }}</router-link>
        <button class="btn ghost small" :title="$t('projects.calendarView')" @click="router.push('/projects/calendar')">
          <MdiCalendarMonth class="w-4 h-4" />
        </button>
        <button v-if="isOperatorOrAbove" class="btn primary sm" @click="router.push('/project/new')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          {{ $t('common.new') }}
        </button>
      </template>
    </BasePageHeader>

    <!-- Search -->
    <div class="px-4 pt-3.5 pb-2">
      <SearchField v-model="query" :debounce="0" :placeholder="$t('projects.searchPlaceholder')" />
    </div>

    <!-- Quick filters -->
    <div class="flex gap-1.5 px-4 pt-2 pb-1 overflow-x-auto [scrollbar-width:none] [-webkit-overflow-scrolling:touch]">
      <button
        v-for="f in QUICK_FILTERS"
        :key="f.value"
        :class="['flex-shrink-0 px-3 py-1.5 rounded-full border text-[.8rem] font-medium cursor-pointer transition-all', quickFilter === f.value ? 'bg-blue-700 border-blue-500 text-white' : 'bg-gray-800 border-gray-600 text-gray-400 hover:bg-[#273344] hover:border-gray-500 hover:text-gray-200']"
        @click="quickFilter = f.value"
      >{{ $t('projects.filters.' + f.value) }}</button>
    </div>

    <!-- Filters -->
    <div class="flex gap-2 px-4 py-1">
      <BaseSelect v-model="customerFilter" class="flex-1" density="compact" :aria-label="$t('projects.allCustomers')">
        <option value="">{{ $t('projects.allCustomers') }}</option>
        <option v-for="c in customers" :key="c.id" :value="String(c.id)">{{ c.name }}</option>
      </BaseSelect>
    </div>
    <div class="flex gap-2 px-4 pb-2">
      <BaseInput type="date" v-model="dateFrom" class="flex-1" density="compact" :aria-label="$t('projects.startDateFrom')" :title="$t('projects.startDateFrom')" />
      <BaseInput type="date" v-model="dateTo" class="flex-1" density="compact" :aria-label="$t('projects.startDateTo')" :title="$t('projects.startDateTo')" />
    </div>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />
    <EmptyState
      v-else-if="!filtered.length"
      :message="(query || quickFilter !== 'active' || customerFilter) ? $t('projects.noMatch') : $t('projects.noActive')"
    />

    <div v-else class="list-stack">
      <div
        v-for="project in filtered"
        :key="project.id"
        class="data-card cursor-pointer"
        @click="openProject(project)"
      >
        <div class="data-card__main">
          <div class="data-card__name">{{ project.name }}</div>
          <div class="data-card__meta">
            <span v-if="project.start_time" class="badge badge-accent">
              {{ formatDate(project.start_time) }} – {{ formatDate(project.end_time) }}
            </span>
            <span v-if="customerName(project)" class="badge">
              {{ customerName(project) }}
            </span>
            <span v-if="project.location" class="badge badge-warning">
              {{ project.location }}
            </span>
            <span
              v-if="project.status != null"
              class="badge"
              :class="{
                'badge-muted':   Number(project.status) === 0,
                'badge-accent':  Number(project.status) === 1,
                'badge-success': [2, 5, 7].includes(Number(project.status)),
                'badge-warning': [3, 4, 6].includes(Number(project.status)),
                'badge-danger':  Number(project.status) === 8,
              }"
            >{{ projectStatusLabel(project.status, project.status_display) }}</span>
          </div>
        </div>

        <div class="flex items-center gap-2.5 shrink-0">
          <!-- Pick list progress pill if available -->
          <div v-if="project.pick_progress != null" class="relative w-14 h-2 bg-gray-700 rounded overflow-hidden">
            <div
              class="absolute inset-y-0 left-0 rounded transition-all"
              :style="{ width: project.pick_progress + '%' }"
              :class="project.pick_progress === 100 ? 'bg-green-600' : 'bg-blue-500'"
            />
            <span class="absolute inset-0 flex items-center justify-center text-[.6rem] font-bold text-gray-100">{{ project.pick_progress }}%</span>
          </div>

          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-gray-500">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { useStatusLabels } from '@/composables/useStatusLabels.js'
const { projectStatusLabel } = useStatusLabels()

import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { getProjects, getCustomers } from '@/api/api.js'
import { usePermissions } from '@/composables/usePermissions.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import MdiCalendarMonth from '~icons/mdi/calendar-month'
import SearchField from '@/components/search/SearchField.vue'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const route  = useRoute()
const pickMode = computed(() => route.query.mode === 'pick')
const { isOperatorOrAbove } = usePermissions()
const { t } = useI18n()

const projects       = ref([])
const customers      = ref([])   // full list for dropdown
const customerLookup = ref({})   // id → name
const loading        = ref(false)
const loadError      = ref(null)
const query          = ref('')
const customerFilter = ref('')
const quickFilter    = ref('active')
const dateFrom       = ref('')
const dateTo         = ref('')

const QUICK_FILTERS = computed(() => [
  { value: 'active',   label: t('projects.status.active') },
  { value: 'upcoming', label: t('projects.filters.upcoming') },
  { value: 'all',      label: t('projects.filters.all') },
])

function customerName(project) {
  const id = typeof project.customer === 'object' && project.customer !== null
    ? project.customer.id
    : project.customer
  return id ? (customerLookup.value[id] ?? null) : null
}

const filtered = computed(() => {
  let list = projects.value

  if (quickFilter.value === 'active') {
    list = list.filter(p => { const s = Number(p.status); return s !== 7 && s !== 8 })
  } else if (quickFilter.value === 'upcoming') {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    list = list.filter(p => p.start_time && new Date(p.start_time) >= today && Number(p.status) !== 8)
  }
  // 'all' — no status filtering

  if (customerFilter.value) list = list.filter(p => {
    const id = typeof p.customer === 'object' && p.customer !== null ? p.customer.id : p.customer
    return String(id) === customerFilter.value
  })

  if (dateFrom.value) {
    const from = new Date(dateFrom.value)
    list = list.filter(p => p.start_time && new Date(p.start_time) >= from)
  }
  if (dateTo.value) {
    const to = new Date(dateTo.value)
    to.setHours(23, 59, 59, 999)
    list = list.filter(p => p.start_time && new Date(p.start_time) <= to)
  }

  const q = query.value.trim().toLowerCase()
  if (q) {
    list = list.filter(p => {
      const cname = customerName(p)?.toLowerCase() ?? ''
      return (
        p.name?.toLowerCase().includes(q) ||
        p.location?.toLowerCase().includes(q) ||
        cname.includes(q)
      )
    })
  }

  // Sort by start_time ascending (no date → end of list)
  list = [...list].sort((a, b) => {
    if (!a.start_time && !b.start_time) return 0
    if (!a.start_time) return 1
    if (!b.start_time) return -1
    return new Date(a.start_time) - new Date(b.start_time)
  })

  return list
})

const STATUS_CLASSES = {
  0: 'project-card__pill--status-draft',
  1: 'project-card__pill--status-confirmed',
  2: 'project-card__pill--status-ready',
  3: 'project-card__pill--status-pickedup',
  4: 'project-card__pill--status-delivered',
  5: 'project-card__pill--status-active',
  6: 'project-card__pill--status-returning',
  7: 'project-card__pill--status-completed',
  8: 'project-card__pill--status-cancelled',
}

function statusClass(status) {
  return STATUS_CLASSES[status] ?? ''
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

async function load() {
  loading.value   = true
  loadError.value = null
  try {
    const [projRes, custRes] = await Promise.all([
      getProjects(),
      getCustomers(),
    ])
    projects.value = projRes.results ?? projRes ?? []
    const custList = custRes.results ?? custRes ?? []
    customers.value = custList
    const lookup = {}
    for (const c of custList) lookup[c.id] = c.name
    customerLookup.value = lookup
  } catch (e) {
    loadError.value = t('projects.loadFailed')
  } finally {
    loading.value = false
  }
}

function openProject(project) {
  if (pickMode.value) {
    router.push(`/project/${project.id}/pick`)
  } else {
    router.push(`/project/${project.id}`)
  }
}

onMounted(load)
</script>


